/* eslint-disable max-len */
import React, { useEffect, useState, useRef } from 'react';
import i18n from 'i18next';
import { handleAxiosError, useModals } from 'core/notifications';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import screenfull from 'screenfull';

import getVideoProviderByUrl from '../helpers/getVideoProviderByUrl';

import VideoBookmark from '../components/videoBookmark';
import getAxiosConfig from '../helpers/getAxiosConfig';

const COMPLETION_PADDING_PERCENTAGE = 90;

export default ({
  createEnrollment = () => {},
  updateEnrollment,
  enrollmentData,
  video,
  id,
  shouldNotAutoplay,
  shouldNotEnroll,
  selectedAgendaItem,
}) => {
  const isScreenfullEnabled = screenfull.isEnabled;
  const [pip, setPip] = useState(false);
  const [playing, setPlaying] = useState(!shouldNotAutoplay);
  const [muted, setMuted] = useState(!shouldNotAutoplay);
  const [volume, setVolume] = useState(0);
  const [played, setPlayed] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.0);
  const [fullscreen, setFullscreen] = useState(screenfull.isFullscreen);
  const [captionsMode, setCaptionsMode] = useState('hidden');
  const [isNextupVideoShowing, setIsNextupVideoShowing] = useState(false);
  const [enrollmentComplete, setEnrollmentComplete] = useState(false);

  const ref = useRef(null);
  const intervalRef = useRef(null);

  const { addModal } = useModals();

  const queryClient = useQueryClient();

  useEffect(() => {
    const launchVideo = async () => {
      try {
        if (id && selectedAgendaItem) {
          await axios(getAxiosConfig('launch-video', { id, agendaItemId: selectedAgendaItem.id }));
        }
      } catch (error) {
        handleAxiosError(error);
      }
    };
    launchVideo();
  }, [id, selectedAgendaItem]);

  // If we have a video without enrollment data, then enroll the user
  // If the enrollment is not complete, then update the enrollment so that the learner can resume exactly from where they left off
  const upsertEnrollment = async () => {
    if (!enrollmentData && video) {
      await createEnrollment();
      await queryClient.refetchQueries({ queryKey: ['video', id.toString()] });
    }

    if (enrollmentData && !enrollmentData.is_complete && !enrollmentComplete && duration > 0) {
      const currentTime = ref.current.getCurrentTime();
      const completionTime = (duration / 100) * COMPLETION_PADDING_PERCENTAGE;
      const isComplete = currentTime >= completionTime;

      await updateEnrollment({
        isComplete,
        time: currentTime,
        progress: (currentTime / duration) * 100.0,
      });
      setEnrollmentComplete(isComplete);
    }
  };

  // Reset states when video changes
  useEffect(() => {
    setPip(false);
    setMuted(!shouldNotAutoplay);
    setPlaying(!shouldNotAutoplay);
    setVolume(0);
    setPlayed(0);
    setDuration(0);
    setPlaybackRate(1.0);
    setIsNextupVideoShowing(false);

    return () => {
      // Cleanup function to be executed on unmount
      // Clear any timers or ongoing tasks here (in this case, the interval)
      clearInterval(intervalRef.current);
    };
  }, [video]);

  // Set an interval for every 1 second to update a learners enrollment progress
  // Enrollment shouldn't be updated for enrollment has already been completed
  // Enrollment gets set to complete if 90 percent of the video has been played
  useEffect(() => {
    if (!enrollmentData || duration === 0 || !playing) {
      return;
    }

    const interval = setInterval(async () => {
      upsertEnrollment();
    }, 1000);

    intervalRef.current = interval;

    return () => clearInterval(interval);
  }, [playing, duration, enrollmentData]);

  // When the component mounts, update the seek bar to reflect where learner should resume from
  useEffect(() => {
    const params = new URL(document.location).searchParams;
    const timeStamp = parseInt(params.get('t'), 10);
    const resumeTime = timeStamp || enrollmentData?.time;

    if (resumeTime) {
      setPlayed(resumeTime);
    }
  }, [enrollmentData]);

  useEffect(() => {
    if (ref.current?.getInternalPlayer()) {
      const tracks = ref.current.getInternalPlayer().textTracks;
      const defaultMode = 'hidden';

      if (tracks && tracks.length > 0) {
        const captionsTrack = tracks[0];
        captionsTrack.mode = defaultMode;
      }
    }
  }, [ref.current?.getInternalPlayer()]);

  useEffect(() => {
    if (!isScreenfullEnabled) return;

    const handleScreenfullChange = () => setFullscreen(screenfull.isFullscreen);

    screenfull.on('change', handleScreenfullChange);

    return () => screenfull.off('change', handleScreenfullChange);
  }, [isScreenfullEnabled]);

  const handleTogglePlaying = async ({ shouldPlay } = {}) => {
    setPlaying(shouldPlay !== undefined ? shouldPlay : !playing);
    // shouldPlay is only passed in when the player itself invokes this function
    // (such as when clicking on the video itself, or as a side effect of clicking the controls)
    // When the controls are clicked, shouldPlay is undefined, so we don't want to upsertEnrollment
    // twice.
    if (!shouldNotEnroll && shouldPlay !== undefined) await upsertEnrollment();
  };

  const handleSeekChange = (value) => {
    const currentTime = ref.current.getCurrentTime();
    ref.current.seekTo(currentTime + parseFloat(value));
  };

  const handleSetPlaybackRate = () => {
    const playbackRates = [0.5, 1.0, 1.25, 1.5, 1.75, 2.0];
    const currentPlayBackRateIndex = playbackRates.findIndex((element) => element === playbackRate);
    const isLastPlaybackRate = currentPlayBackRateIndex + 1 === playbackRates.length;
    const newPlayBackRateIndex = isLastPlaybackRate ? 0 : currentPlayBackRateIndex + 1;

    setPlaybackRate(playbackRates[newPlayBackRateIndex]);
  };

  const handleDuration = (durationTime) => {
    setDuration(durationTime);
  };

  const handleProgress = (state) => {
    if (playing) {
      setPlayed(state.playedSeconds);
    }
  };

  const handleFullScreen = () => {
    if (isScreenfullEnabled) {
      screenfull.toggle(document.getElementById(1));
    }
  };

  const handleTogglePIP = () => {
    setPip(!pip);
  };

  const handleSeekBarChange = (e) => {
    ref.current.seekTo(e / 1000);
    setPlayed(parseFloat(e / 1000));
  };

  const handleVolumeChange = (value) => {
    const volumeValue = parseFloat(value);
    if (volumeValue) setMuted(false);
    setVolume(volumeValue);
  };

  const handleToggleCaptions = async () => {
    const player = ref.current.getInternalPlayer;

    const provider = getVideoProviderByUrl({ videoUrl: video });

    switch (provider) {
      case 'file': {
        const captionsTrack = player().textTracks?.[0];

        if (!captionsTrack) return;

        const newMode = captionsTrack.mode === 'showing' ? 'hidden' : 'showing';

        captionsTrack.mode = newMode;

        setCaptionsMode(newMode);

        break;
      }
      case 'youtube': {
        if (player().loadModule && captionsMode === 'hidden') {
          player().loadModule('captions');
          player().setOption('captions', 'track', { languageCode: 'en' });
          setCaptionsMode('showing');
        } else if (player().unloadModule && captionsMode === 'showing') {
          player().unloadModule('captions');
          setCaptionsMode('hidden');
        }
        break;
      }
      case 'vimeo': {
        const textTracks = await player().getTextTracks();

        let lang = 'en';

        if (!textTracks.some((item) => item.language === 'en')) lang = textTracks[0].language;

        if (player().enableTextTrack && captionsMode === 'hidden') {
          await player().enableTextTrack(lang);
          setCaptionsMode('showing');
        } else if (player().disableTextTrack && captionsMode === 'showing') {
          await player().disableTextTrack(lang);
          setCaptionsMode('hidden');
        }
        break;
      }
      default:
    }
  };

  const handleBookmark = async () => {
    addModal({
      hasCloseButton: true,
      title: i18n.t('bookmark-link-title'),
      component: <VideoBookmark timestamp={played} videoId={id} />,
      id: `video-bookmark-${id}`,
    });
  };

  const handleNextupVideoPopup = (isShowing) => {
    setIsNextupVideoShowing(isShowing);
    setPlaying(false);
  };

  return {
    handleTogglePlaying,
    handleSeekChange,
    handleSetPlaybackRate,
    handleDuration,
    handleProgress,
    handleFullScreen,
    handleTogglePIP,
    handleSeekBarChange,
    handleVolumeChange,
    handleToggleCaptions,
    handleBookmark,
    handleNextupVideoPopup,
    muted,
    pip,
    playing,
    volume,
    played,
    playbackRate,
    duration,
    fullscreen,
    captionsMode,
    ref,
    isNextupVideoShowing,
  };
};
